import * as React from 'react';
import { useId } from '@mantine/hooks';
import { NumericFormat, NumericFormatProps, NumberFormatValues } from 'react-number-format';
import { Input, createStyles, InputProps } from '@mantine/core';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        readonlyInput: commonFieldStyles.readonlyInput,
        leftGroupPosition: commonFieldStyles.leftGroupPosition,
        rightGroupPosition: commonFieldStyles.rightGroupPosition,
        middleGroupPosition: commonFieldStyles.middleGroupPosition,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description
    };
});

export interface NumericFormatInputProps extends InputProps, GenericFieldProps {
    key?: string;
    label?: string;
    prefix?: NumericFormatProps['prefix'];
    suffix?: NumericFormatProps['suffix'];
    decimalScale?: NumericFormatProps['decimalScale'];
    isAllowed?: NumericFormatProps['isAllowed'];
    readOnly?: boolean;
    value?: string;
    thousandSeparator?: string;
    width?: string;
    placeholder?: string;
    onChange?: (val: number) => void;
    onValueChange?: (values: NumberFormatValues) => void;
    allowNegative?: boolean;
}

export const NumericFormatInput: React.FC<NumericFormatInputProps> = ({
    readOnly,
    labelKey,
    label,
    error,
    descriptionKey,
    tooltipKey,
    required,
    groupPosition,
    onChange,
    value,
    thousandSeparator,
    width,
    placeholder,
    parentform,
    targetperiod,
    highlight,
    sumOf,
    ...props
}) => {
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const id = useId();

    return (
        <Input.Wrapper
            id={id}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            required={required}
            style={width ? { width } : {}}
            classNames={{
                root: classes.root,
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                error: classes.error,
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            error={error}
        >
            <Input<any>
                component={NumericFormat}
                classNames={{
                    input: cx(classes.input, {
                        [classes.readonlyInput]: readOnly,
                        [classes.leftGroupPosition]: groupPosition && groupPosition === 'l',
                        [classes.rightGroupPosition]: groupPosition && groupPosition === 'r',
                        [classes.middleGroupPosition]: groupPosition && groupPosition === 'm',
                        [classes.inputHighlight]: typeof highlight === 'string' && highlight === 'true'
                    }),
                    icon: cx(classes.icon, {
                        [classes.hiddenDisplay]: readOnly
                    }),
                    wrapper: cx(classes.wrapper, {
                        [classes.readonlyWrapper]: readOnly
                    })
                }}
                id={id}
                disabled={readOnly}
                error={error}
                // onChange={onChange}
                value={value}
                placeholder={placeholder}
                thousandSeparator={thousandSeparator}
                onValueChange={(values: NumberFormatValues) => {
                    onChange?.(values.floatValue ?? 0);
                }}
                {...props}
            />
        </Input.Wrapper>
    );
};
